import React from 'react';
import { Divider, Typography, colors } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AnchorLink from '@/common/AnchorLink';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(3),
    '& p': {
      marginBottom: theme.spacing(3)
    },
    '& h2': {
      marginBottom: theme.spacing(2)
    }
  },
  link: {
    color: colors.blue[500],
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  list: {
    marginTop: 0
  }
}));

const CommunityAffairsPage = (): JSX.Element | null => {
  const classes = useStyles();

  return (
    <div className={classes.root} data-testid="supplier-code-of-conduct-page">
      <Typography variant="h1" gutterBottom>
        Community Affairs Support
      </Typography>
      <Typography paragraph gutterBottom>
        As part of a global company, Pizza Hut, LLC and its franchise partners aim to make the world better by acting
        responsibly in regard to our food, planet and people. The Pizza Hut system believes in the importance of
        supporting the communities we operate in, and give back through various channels including sponsorships, in-kind
        donations and volunteerism.
        <br />
        <br />
        Pizza Hut and its franchise partners receive many funding and donation requests every year, and unfortunately
        cannot support or respond to every request. The brief guidelines below are set to help organizations understand
        our priorities and procedures, but does not outline all factors such as time and resources requests.
      </Typography>
      <Divider />
      <br />
      <Typography variant="h2" gutterBottom>
        Corporate Level Support
      </Typography>
      <Typography paragraph>
        Pizza Hut believes education is the key to unlocking a person’s full potential and that educational success is
        rooted in childhood reading skills. Since 1984, Pizza Hut has nurtured a love of reading through the{' '}
        <AnchorLink
          className={classes.link}
          link="http://www.bookitprogram.com/"
          external
          linkDisplayText="BOOK IT!® Program"
        />{' '}
        – inspiring 1-in-5 Americans along the way! Now, through{' '}
        <AnchorLink
          className={classes.link}
          link="https://theliteracyproject.pizzahut.com/"
          external
          linkDisplayText="The Literacy Project"
        />{' '}
        we have an even greater goal – to transform the lives of 100 million people in just 10 years, by enabling access
        to books and educational resources, empowering teachers and inspiring readers.
      </Typography>
      <Typography paragraph>
        In addition to The Literacy Project, Pizza Hut also supports hunger-related initiatives through its Harvest
        Program. The Harvest Program, founded in 1992, was created to help reduce waste by donating surplus food to
        those in need in the communities we serve.
      </Typography>
      <Typography paragraph>
        If you are a national organization and are seeking a possible relationship with the Pizza Hut system, or are
        seeking support for a Dallas-Fort Worth based organization, please{' '}
        <AnchorLink
          className={classes.link}
          link="mailto:theliteracyproject@pizzahut.com?subject=community support request-pizzahut.com"
          external
          linkDisplayText="email your request"
        />
        . Include your solicitation letter/request, organization’s exemption letter and contact information. Email
        requests will only be considered - mailed letters to the Pizza Hut corporate office will not be evaluated.
      </Typography>
      <Typography component="span">
        Pizza Hut, LLC does not provide community support at the corporate level to any of the following:
      </Typography>
      <ul className={classes.list}>
        <li>Individuals</li>
        <li>Profit-making entities</li>
        <li>Athletic groups or activities</li>
        <li>Endowments, capital campaigns or memorials</li>
        <li>Travel fees for school groups or individuals</li>
        <li>Political organizations, campaigns or candidates</li>
        <li>Medical care or donations</li>
      </ul>
      <br />
      <Typography variant="h2" gutterBottom>
        Franchise Level Support
      </Typography>
      <Typography paragraph>
        In addition to PHLLC’s community support, Pizza Hut’s franchise partners generously support the communities in
        which they operate. Because needs and opportunities vary within communities, please contact your local Pizza Hut
        with your request. The local restaurant/franchise organization will determine if they are able to provide
        support. PHLLC does not directly influence or make decisions at the local level.
      </Typography>
      <Typography variant="h2" gutterBottom>
        Thank You
      </Typography>
      <Typography paragraph>Thank you for your consideration and cooperation with the guidelines above.</Typography>
    </div>
  );
};

export default CommunityAffairsPage;
